import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  computed,
  signal
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AlertBlock } from '@core/services/ai/ai-assistant.service';
import { normalizeQueryParams, safeInAppRoute } from '../safe-route.helper';

const DEFAULT_ICON: Record<AlertBlock['severity'], string> = {
  info: 'info',
  success: 'check_circle',
  warning: 'warning',
  danger: 'error'
};

/**
 * Renders an `alert` widget. The optional CTA has two modes:
 *   - `action.route` present → an in-app `[routerLink]` (navigation). The
 *     `/`-prefix guard from `safe-route.helper` rejects external URLs.
 *   - otherwise `action.message` → a button that re-injects the message
 *     as a new user turn (via `actionInvoked`).
 * When both are present, `route` wins (navigation).
 */
@Component({
  selector: 'app-ai-alert-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, RouterLink],
  template: `
    <div class="alert" [attr.data-severity]="block.severity">
      <mat-icon class="alert__icon">{{ block.icon || icon() }}</mat-icon>
      <div class="alert__body">
        <div class="alert__title">{{ block.title }}</div>
        @if (block.body) {
          <div class="alert__msg">{{ block.body }}</div>
        }
      </div>
      @if (block.action) {
        @if (hasRoute()) {
          <a
            class="alert__cta"
            mat-flat-button
            [routerLink]="safeRoute()"
            [queryParams]="normalizedQueryParams()">
            {{ resolvedLabel() }}
            <mat-icon class="alert__cta-arrow">arrow_forward</mat-icon>
          </a>
        } @else {
          <button
            type="button"
            mat-flat-button
            class="alert__cta"
            (click)="onAction()">
            {{ resolvedLabel() }}
          </button>
        }
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .alert {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 12px;
        border: 1px solid rgba(99, 102, 241, 0.25);
        border-left: 4px solid #6366f1;
        background: rgba(99, 102, 241, 0.06);
        color: rgba(15, 23, 42, 0.92);
      }
      .alert[data-severity='success'] {
        border-color: rgba(16, 185, 129, 0.25);
        border-left-color: #10b981;
        background: rgba(16, 185, 129, 0.07);
      }
      .alert[data-severity='warning'] {
        border-color: rgba(245, 158, 11, 0.25);
        border-left-color: #f59e0b;
        background: rgba(245, 158, 11, 0.08);
      }
      .alert[data-severity='danger'] {
        border-color: rgba(239, 68, 68, 0.25);
        border-left-color: #ef4444;
        background: rgba(239, 68, 68, 0.08);
      }
      .alert__icon {
        font-size: 22px;
        width: 22px;
        height: 22px;
        flex-shrink: 0;
        color: #6366f1;
      }
      .alert[data-severity='success'] .alert__icon {
        color: #10b981;
      }
      .alert[data-severity='warning'] .alert__icon {
        color: #f59e0b;
      }
      .alert[data-severity='danger'] .alert__icon {
        color: #ef4444;
      }
      .alert__body {
        flex: 1;
        min-width: 0;
      }
      .alert__title {
        font-weight: 700;
        font-size: 14px;
        overflow-wrap: anywhere;
      }
      .alert__msg {
        margin-top: 2px;
        font-size: 13px;
        color: rgba(15, 23, 42, 0.75);
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
      .alert__cta {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .alert__cta-arrow {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    `
  ]
})
export class AiAlertBlockComponent {
  private readonly _block = signal<AlertBlock | null>(null);

  /**
   * Constructor injection (over `inject()`) so the component can be
   * instantiated with `new` in pure unit tests without TestBed. When
   * `translate` is null, the i18n key falls back to the server label.
   */
  constructor(
    @Optional() private readonly translate: TranslateService | null = null
  ) {}

  @Input({ required: true }) set block(value: AlertBlock) {
    this._block.set(value);
  }
  get block(): AlertBlock {
    return this._block()!;
  }

  @Output() readonly actionInvoked = new EventEmitter<string>();

  /** True when the action should navigate (route present) rather than re-ask. */
  readonly hasRoute = computed<boolean>(() => {
    const route = this._block()?.action?.route;
    return typeof route === 'string' && route.length > 0;
  });

  readonly safeRoute = computed(() =>
    safeInAppRoute(this._block()?.action?.route)
  );

  readonly normalizedQueryParams = computed(() =>
    normalizeQueryParams(this._block()?.action?.queryParams)
  );

  readonly resolvedLabel = computed<string>(() => {
    const action = this._block()?.action;
    if (!action) return '';
    return this.resolveKey(action.labelKey, action.label);
  });

  private resolveKey(key: string | undefined, fallback: string): string {
    if (!key) return fallback;
    if (!this.translate) return fallback;
    const translated = this.translate.instant(key);
    if (typeof translated !== 'string' || translated === key) return fallback;
    return translated;
  }

  icon(): string {
    const severity = this._block()?.severity ?? 'info';
    return DEFAULT_ICON[severity];
  }

  onAction(): void {
    const message = this._block()?.action?.message;
    if (message) {
      this.actionInvoked.emit(message);
    }
  }
}
